import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import Page from '../../lib/page';
import { modalChannels } from '../constants/UtilVariables';
import { trackPage } from '../../lib/analytics-helper';
import { initiateAsyncChat, isMessageUsAvailable } from '../../lib/asyncMessageHelper';
import poll from '../../lib/poll';
import { nodeChatOnPageValidator } from '../../lib/node-chat-on-page-validator';
import fireImpressionFptiForChatLoadError from '../../lib/fire-impression-fpti-for-chat-load-error';
import { asyncOrigins, chatBotMessageUsRefIdsMapping } from '../../common/commonConstants';

// redirect to appropriate channel after checking the IDI response.
export function initializeAppropriateChannel(topic = {}, redirectChannel) {
  if (_isEmpty(redirectChannel)) {
    return trackPage({ id: 'chatMessageUsChatBotLoadError', link: `chat-chatbot-messageUs-loading-error|redirect-channel-${redirectChannel}` });
  }

  const topicRefId = topic && topic.topicRefId ? `${topic.topicRefId}|${chatBotMessageUsRefIdsMapping.FINAL_INTENT}` : chatBotMessageUsRefIdsMapping.GENPOOL_CONTACT_US;

  // Async - MESSAGE US
  if (redirectChannel === modalChannels.messageUs) {
    const asyncOrigin = topicRefId.includes(chatBotMessageUsRefIdsMapping.FINAL_INTENT)
      ? asyncOrigins.FINAL_CONTACT_OPTIONS : asyncOrigins.DEFAULT_CONTACT_OPTIONS;
    return initiateAsyncChat({ topicRefId, origin: asyncOrigin });
  }

  // If chat is available in IDI response live chat will be opened if not chat bot will be opened.
  if (redirectChannel === modalChannels.chatBot || redirectChannel === modalChannels.liveChat) {
    // Waits at least 3.20 secs based on current retries = 5 and delay = 200 ms
    return poll(nodeChatOnPageValidator, 5, 200)
      .then(() => {
        // eslint-disable-next-line no-undef
        nodeChat.show(redirectChannel, topicRefId);
      })
      .catch(() => fireImpressionFptiForChatLoadError());
  }

  return trackPage({ id: 'chatMessageUsChatBotLoadError', link: `chat-chatbot-messageUs-loading-error|redirect-channel-${redirectChannel}` });
}

/**
    IDI eligibility checker to wait for IDI response and to check the availability of channels.
    Once the IDI response is available these are the sequence of checks
      1) If async user checks if MESSAGE_US is available - if available returns messageUS
      2) If non async user checks if CHAT is available  - if available returns chat
      3) if both the channels are not available check the postchatbot eligibility response
          If ELIGIBLE return chat bot
          if INELIGIBLE show ineligible modal window.
 * */
export function idiChatEligibilityChecker(topicId, fromChannel, showChat, reject) {
  return (dispatch, getState) => {
    // Time out to keep checking IDI contact options.
    const timeOutIDICheck = window.location.href.includes('localhost') ? 1500 : 1000;

    const callback = (params) => {
      if (!params.eligible) {
        return dispatch(reject()); // Show error message, if the Chat eligibilty is failed after logged in
      }

      return showChat({ topicRefId: topicId }, params.redirectChannel);
    };

    const checkPostChatBotEligibility = (state) => {
      const { postChatBotEligibility: { isLoading, chatBotDisplay } } = state;
      if (!isLoading && chatBotDisplay) {
        return {
          eligible: true,
          redirectChannel: modalChannels.chatBot,
        };
      }

      return { eligible: false };
    };

    const checker = (idiChannelDecisions, state) => {
      // If no IDI channels check for post chat bot eligibility.
      if (!idiChannelDecisions || !Array.isArray(idiChannelDecisions)) {
        // return post chat bot eligibility decision
        return checkPostChatBotEligibility(state);
      }

      // First check if MESSAGE_US is available
      if (isMessageUsAvailable(idiChannelDecisions)) {
        return {
          eligible: true,
          redirectChannel: modalChannels.messageUs,
        };
      }

      // Second check if CHAT is available
      const chatAvailable = _find(idiChannelDecisions, (channel) => channel.name === 'CHAT');
      if (chatAvailable && chatAvailable.availability_decision === 'AVAILABLE') {
        return {
          eligible: true,
          redirectChannel: modalChannels.liveChat,
        };
      }

      // Fallback to bot
      return checkPostChatBotEligibility(state);
    };

    // DELAY CHECKER TO KEEP CHECKING FOR IDI CONTACT OPTIONS.
    const delayCheckerForIDIOptions = (retry) => {
      const state = getState();

      if (state.contactChannels.IDIIsLoaded) {
        const finalIntentIDIDecisions = state.contactChannels.IDIAvailability;
        return callback(checker(finalIntentIDIDecisions, state));
      }

      if (retry >= 0) {
        return setTimeout(() => delayCheckerForIDIOptions(retry - 1), timeOutIDICheck);
      }
      return callback(checker(null, state));
    };

    // Final intent
    if (!_isEmpty(topicId)) {
      return delayCheckerForIDIOptions(10); // Wait IDI contact options to finish loading
    }

    // Default
    const defaultIdiChannelDecisions = Page.getIDIContactOptionDecisions();
    return callback(checker(defaultIdiChannelDecisions, getState()));
  };
}

/**
 *  NON IDI market chat eligibility checker.
 *  For non idi markets post chat bot eligibility response is will be checked.
    If ELIGIBLE return chat bot
    if INELIGIBLE show ineligible modal window.

 * @param topicId
 * @param fromChannel
 * @param showChat
 * @param reject
 * @returns {Function}
 */
export function nonIdiChatEligibilityChecker(topicId, fromChannel, showChat, reject) {
  return (dispatch, getState) => {
    // Time out to keep checking IDI contact options.
    const timeOutIDICheck = window.location.href.includes('localhost') ? 1500 : 1000;

    const callback = (params) => {
      if (!params) {
        return dispatch(reject()); // Show error message/ ineligible modal window
      }

      return showChat({ topicRefId: topicId }, modalChannels.chatBot);
    };

    // DELAY CHECKER TO KEEP CHECKING FOR IDI CONTACT OPTIONS.
    const delayCheckerForPostChatBotResponse = (retry) => {
      const { postChatBotEligibility: { isLoading, chatBotDisplay } } = getState();
      if (!isLoading) {
        return callback(chatBotDisplay);
      }

      if (retry >= 0) {
        return setTimeout(() => delayCheckerForPostChatBotResponse(retry - 1), timeOutIDICheck);
      }
      return callback(chatBotDisplay);
    };

    delayCheckerForPostChatBotResponse(10); // Wait for post chat bot eligibility call to finish loading
  };
}
